import React, { useState, useEffect } from "react";
import { useL } from "@/lib/useL";
import { Sparkles, ArrowRight, X, Zap, Clock, Target } from "lucide-react";
import { trackEvent } from "@/lib/analytics";

const STORAGE_KEY = "sux1_onboarding_dismissed";

const EXAMPLES_EN = [
  { id: "news", label: "News article", icon: "📰", text: "As housing becomes one of the major themes of European political debate, a study published on Monday by the French statistics office shows that property prices rose by 4.8% in France over the past year, with large differences between regions. Big cities such as Paris, Lyon and Bordeaux are seeing their markets slow down, while medium-sized towns are attracting a new generation of remote workers. Economists agree that this lasting shift could reshape the French urban model, especially in terms of transport and access to public services." },
  { id: "course", label: "Lesson extract", icon: "🎓", text: "Photosynthesis is the process by which plants, algae and some bacteria convert light energy from the sun into chemical energy stored in glucose. It mainly takes place in the chloroplasts of plant cells, where chlorophyll captures photons. The overall reaction combines carbon dioxide (CO2) and water (H2O) to produce glucose (C6H12O6) and release oxygen (O2). There are two phases: the light phase, which depends on light and produces ATP and NADPH; and the dark phase, or Calvin cycle, which uses these molecules to fix CO2 into sugars. Photosynthesis is essential to life on Earth: it is the basis of all food chains and regulates the levels of O2 and CO2 in the atmosphere." },
  { id: "essay", label: "Book extract", icon: "📚", text: "There is but one truly serious philosophical problem, and that is suicide. Judging whether life is or is not worth living amounts to answering the fundamental question of philosophy. All the rest, whether or not the world has three dimensions, whether the mind has nine or twelve categories, comes afterwards. These are games; one must first answer. And if it is true, as Nietzsche claims, that a philosopher, to deserve our respect, must preach by example, you can appreciate the importance of that reply, for it will precede the definitive act." },
];

const EXAMPLES = [
  {
    id: "news",
    label: "Article de presse",
    icon: "📰",
    text:
      "Alors que la question du logement devient l'un des thèmes majeurs des débats politiques européens, une étude publiée ce lundi par l'INSEE révèle que les prix de l'immobilier ont progressé de 4,8 % en France sur l'année écoulée, avec des disparités importantes selon les régions. Les grandes métropoles — Paris, Lyon, Bordeaux — voient leur marché ralentir, tandis que les villes moyennes attirent une nouvelle génération de télétravailleurs. Les économistes s'accordent à dire que cette recomposition durable pourrait rebattre les cartes du modèle urbain français, notamment en matière de mobilité et d'accès aux services publics.",
  },
  {
    id: "course",
    label: "Extrait de cours",
    icon: "🎓",
    text:
      "La photosynthèse est le processus par lequel les plantes, les algues et certaines bactéries convertissent l'énergie lumineuse du soleil en énergie chimique stockée dans le glucose. Ce mécanisme se déroule principalement dans les chloroplastes des cellules végétales, où la chlorophylle capte les photons. La réaction globale combine du dioxyde de carbone (CO₂) et de l'eau (H₂O) pour produire du glucose (C₆H₁₂O₆) et libérer de l'oxygène (O₂). On distingue deux phases : la phase claire, dépendante de la lumière, qui produit ATP et NADPH ; et la phase sombre, ou cycle de Calvin, qui utilise ces molécules pour fixer le CO₂ en sucres. La photosynthèse est essentielle à la vie sur Terre : elle fournit la base de toutes les chaînes alimentaires et régule la concentration atmosphérique en O₂ et CO₂.",
  },
  {
    id: "essay",
    label: "Extrait de livre",
    icon: "📚",
    text:
      "Il n'y a qu'un problème philosophique vraiment sérieux : c'est le suicide. Juger que la vie vaut ou ne vaut pas la peine d'être vécue, c'est répondre à la question fondamentale de la philosophie. Le reste, si le monde a trois dimensions, si l'esprit a neuf ou douze catégories, vient ensuite. Ce sont des jeux ; il faut d'abord répondre. Et s'il est vrai, comme le veut Nietzsche, qu'un philosophe, pour être estimable, doit prêcher d'exemple, on comprend l'importance de cette réponse, puisqu'elle va précéder le geste définitif. Ce sont là des évidences sensibles au cœur, mais qu'il faut approfondir pour les rendre claires à l'esprit.",
  },
];

/**
 * Onboarding banner shown to freshly-registered users who haven't yet
 * generated a single summary. Its purpose is to compress the "what am I
 * supposed to paste?" hesitation into a single tap: pick an example →
 * textarea auto-fills → Résumer becomes obvious. Attacks the −97 %
 * drop-off between signup and first summary observed in /admin.
 *
 * Props:
 * - onPickExample(text: string): parent handler that fills the textarea
 * - onDismiss(): optional; parent can also react to dismiss
 */
export function OnboardingCTA({ onPickExample, onDismiss }) {
  const L = useL();
  const examples = L(EXAMPLES, EXAMPLES_EN);
  const [dismissed, setDismissed] = useState(true);

  useEffect(() => {
    try {
      const val = localStorage.getItem(STORAGE_KEY);
      setDismissed(val === "1");
    } catch (_) {
      setDismissed(false);
    }
    trackEvent("onboarding_shown");
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {}
    setDismissed(true);
    trackEvent("onboarding_dismissed");
    if (onDismiss) onDismiss();
  };

  const pick = (ex) => {
    trackEvent("onboarding_example_picked", { example_id: ex.id });
    onPickExample(ex.text);
    try {
      localStorage.setItem(STORAGE_KEY, "1");
    } catch (_) {}
    setDismissed(true);
  };

  if (dismissed) return null;

  return (
    <div
      data-testid="onboarding-cta"
      className="mx-6 md:mx-8 lg:mx-10 mt-6 border-2 border-[#002FA7] bg-gradient-to-br from-[#002FA7]/[0.08] via-transparent to-[#002FA7]/[0.03]"
    >
      <div className="p-6 md:p-8 relative">
        <button
          type="button"
          onClick={dismiss}
          data-testid="onboarding-dismiss"
          aria-label={L("Fermer", "Close")}
          className="absolute top-3 right-3 text-muted-foreground hover:text-foreground transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex items-center gap-2 mb-3">
          <Sparkles className="w-4 h-4 text-[#002FA7]" />
          <span className="font-mono uppercase tracking-[0.2em] text-[10px] text-[#002FA7]">
            {L("Bienvenue sur SUX1", "Welcome to SUX1")}
          </span>
        </div>

        <h3
          className="text-2xl md:text-3xl font-black tracking-tight mb-3 max-w-2xl"
          style={{ fontFamily: "Outfit" }}
        >
          {L("Votre premier résumé en", "Your first summary in")} <span className="text-[#002FA7]">{L("5 secondes.", "5 seconds.")}</span>
        </h3>

        <p className="text-sm md:text-base text-muted-foreground mb-5 max-w-2xl leading-relaxed">
          {L(<>Choisissez un exemple ci-dessous : SUX1 colle le texte, choisit les meilleurs réglages, et vous n&apos;avez plus qu&apos;à cliquer sur <strong>Résumer</strong>.</>,
             <>Pick an example below: SUX1 pastes the text, picks the best settings, and all you have to do is click <strong>Summarise</strong>.</>)}
        </p>

        {/* Feature strip */}
        <div className="grid grid-cols-3 gap-2 mb-6 max-w-xl">
          <div className="flex flex-col items-center text-center gap-1">
            <Zap className="w-4 h-4 text-[#002FA7]" />
            <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              {L("Dès 2 € les 20 résumés", "From €2 for 20 summaries")}
            </div>
          </div>
          <div className="flex flex-col items-center text-center gap-1">
            <Clock className="w-4 h-4 text-[#002FA7]" />
            <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              &lt; 5 sec
            </div>
          </div>
          <div className="flex flex-col items-center text-center gap-1">
            <Target className="w-4 h-4 text-[#002FA7]" />
            <div className="font-mono text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
              6 formats
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {examples.map((ex) => (
            <button
              key={ex.id}
              type="button"
              onClick={() => pick(ex)}
              data-testid={`onboarding-example-${ex.id}`}
              className="group text-left border border-border bg-background hover:border-[#002FA7] hover:bg-[#002FA7]/5 p-4 transition-all"
            >
              <div className="text-2xl mb-2" aria-hidden>
                {ex.icon}
              </div>
              <div className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground mb-1 group-hover:text-[#002FA7]">
                {L("Essayer avec", "Try with")}
              </div>
              <div
                className="font-black text-sm mb-3 group-hover:text-[#002FA7]"
                style={{ fontFamily: "Outfit" }}
              >
                {ex.label}
              </div>
              <div className="flex items-center gap-1 font-mono uppercase tracking-[0.14em] text-[10px] text-[#002FA7]">
                {L("Coller ce texte", "Paste this text")} <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-border/50 flex items-center justify-between gap-3 flex-wrap">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
            {L("Ou collez votre propre texte ci-dessous ↓", "Or paste your own text below ↓")}
          </div>
          <button
            type="button"
            onClick={dismiss}
            data-testid="onboarding-skip"
            className="font-mono uppercase tracking-[0.14em] text-[10px] text-muted-foreground hover:text-foreground underline underline-offset-4"
          >
            {L("Passer", "Skip")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default OnboardingCTA;
